import React from 'react';
import { ExclamationTriangleIcon, LockClosedIcon } from '@heroicons/react/24/outline';

/**
 * Component to display when user doesn't have permission to access a resource
 */
const PermissionDenied = ({
  title = 'Access Denied',
  message = "You don't have permission to access this resource.",
  suggestion = null,
  showIcon = true,
  className = '',
}) => {
  return (
    <div className={`flex flex-col items-center justify-center p-8 text-center ${className}`}>
      {showIcon && (
        <div className="mb-4">
          <LockClosedIcon className="h-16 w-16 text-gray-400" />
        </div>
      )}

      <h3 className="mb-2 text-lg font-semibold text-gray-900">{title}</h3>
      <p className="mb-4 text-gray-600">{message}</p>

      {suggestion && (
        <div className="max-w-md rounded-md bg-yellow-50 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-400" />
            <div className="ml-3">
              <p className="text-sm text-yellow-700">{suggestion}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Component to display when user has no data due to RBAC filtering
 */
export const NoAccessibleData = ({ dataType = 'data', userRole = 'user' }) => {
  const getMessage = () => {
    switch (dataType) {
      case 'training-groups':
        return userRole === 'rower'
          ? 'You are not a member of any training groups yet.'
          : "You don't have access to any training groups.";
      case 'training-sessions':
        return 'You are not assigned to any training sessions yet.';
      case 'crews':
        return userRole === 'rower'
          ? 'You are not a member of any crews yet.'
          : "You don't have access to any crews.";
      default:
        return `No ${dataType} available.`;
    }
  };

  const getSuggestion = () => {
    switch (dataType) {
      case 'training-groups':
        return 'Contact your coach or administrator to be added to a training group.';
      case 'training-sessions':
        return 'Training sessions will appear here once you are assigned to them by your coach.';
      case 'crews':
        return 'Crews will appear here once you are added to them by your coach.';
      default:
        return null;
    }
  };

  return (
    <PermissionDenied
      title={`No ${dataType.replace('-', ' ')} available`}
      message={getMessage()}
      suggestion={getSuggestion()}
      showIcon={false}
      className="min-h-[200px]"
    />
  );
};

/**
 * Higher-order component to wrap content with permission checking
 */
export const withPermissionCheck = (Component, permissionCheck) => {
  return function PermissionCheckedComponent(props) {
    const { user, ...restProps } = props;

    if (!permissionCheck(user)) {
      return (
        <PermissionDenied suggestion="Contact your administrator if you believe you should have access to this feature." />
      );
    }

    return <Component {...restProps} user={user} />;
  };
};

export default PermissionDenied;
